import { cn } from '@virginexperiencedays/tailwind';

//@TODO: Types shouldn't be imported and should be defined independently in the component
import type { Product, Reviews, Location } from '@virginexperiencedays/products';

import { BodyText } from '../../../typography/BodyText';
import { createPromoString } from '../../../utils/sku';
import { getValidityText } from '../../../utils/validity';
import { roundAverageRating } from '../../../utils/productRating';

type ExperienceSummaryType = Pick<Product, 'sku' | 'promocode' | 'validity' | 'numberOfPeople'>;

type Features = {
  locations?: Location[];
  reviews?: Pick<Reviews, 'averageRating' | 'count'>;
} & Partial<ExperienceSummaryType>;

type TransformedFeatures = {
  promo?: string | undefined;
  reviews?: Pick<Reviews, 'averageRating' | 'count'>;
  locations?: Location[];
  numberOfPeople?: number;
  validity?: string;
};

type ExperienceSummaryProps = {
  features: Features;
  className?: string;
  dataTestId?: string;
  isCollectionProduct?: boolean;
};

export const ExperienceSummary = ({
  features,
  className,
  dataTestId = 'experience-summary',
  isCollectionProduct,
}: ExperienceSummaryProps) => {
  if (!features) return null;

  const { sku, promocode, numberOfPeople, validity, reviews, locations } = features;
  const promoString = createPromoString(sku, promocode);
  const validityText = getValidityText(validity?.validityMonths, validity?.expiryDate);
  // Reorder + map promo to sku + promocode
  const mappedFeatures: TransformedFeatures = {
    promo: promoString,
    locations,
    numberOfPeople,
    validity: validityText,
    reviews,
  };

  return (
    <dl
      className={cn('bg-background-page-faded flex flex-wrap gap-6 rounded p-4', className)}
      data-testid={dataTestId}
    >
      {Object.keys(mappedFeatures).map((feature) =>
        featuresFactory(feature, mappedFeatures, isCollectionProduct)
      )}
    </dl>
  );
};

export function featuresFactory(
  feature: string | number,
  object: TransformedFeatures,
  isCollectionProduct: boolean
) {
  switch (feature) {
    case 'promo':
      return (
        object[feature] && (
          <Feature key={feature} data-testid="experience-summary-feature-promo">
            <Title>SKU</Title>
            <Text isCapitalized>{object[feature]}</Text>
          </Feature>
        )
      );
    case 'numberOfPeople':
      return (
        object[feature] && (
          <Feature key={feature} data-testid="experience-summary-feature-people">
            <Title>People</Title>
            <Text isCapitalized>For {object[feature]}</Text>
          </Feature>
        )
      );
    case 'validity':
      return (
        object[feature] && (
          <Feature key={feature} data-testid="experience-summary-feature-validity">
            <Title>Validity</Title>
            <Text>{object[feature]}</Text>
          </Feature>
        )
      );
    case 'reviews':
      return (
        object?.[feature]?.count > 0 &&
        !isCollectionProduct && (
          <Feature key={feature} data-testid="experience-summary-feature-reviews">
            <Title>Rating</Title>
            <Text isCapitalized>
              {object[feature]?.averageRating > 0 && (
                <span>{roundAverageRating(object[feature].averageRating)}</span>
              )}{' '}
              {object[feature]?.count > 0 && <span>({object[feature].count})</span>}
            </Text>
          </Feature>
        )
      );
    case 'locations':
      return (
        !!object[feature]?.length && (
          <Feature key={feature} data-testid="experience-summary-feature-locations">
            <Title>Locations</Title>
            <Text isCapitalized>
              {typeof object[feature]?.[0]?.name === 'string' &&
              object[feature][0].name.toLowerCase().includes('uk wide')
                ? `${object[feature][0].name}`
                : `${object[feature].length} in the UK`}
            </Text>
          </Feature>
        )
      );
    default:
      return null;
  }
}

const Feature = ({ children, ...rest }) => (
  <div className="flex flex-col flex-nowrap gap-1 whitespace-normal" {...rest}>
    {children}
  </div>
);

const Title = ({ children, ...rest }) => (
  <dt className="text-neutral-faded text-sm font-semibold leading-none" {...rest}>
    {children}
  </dt>
);

const Text = ({ isCapitalized = false, children, ...rest }) => {
  return (
    <BodyText
      {...rest}
      as="dd"
      className={cn(
        'text-neutral text-normal ms-0 whitespace-normal leading-none',
        isCapitalized && 'capitalized'
      )}
    >
      {children}
    </BodyText>
  );
};
